"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import LandingFiltersPanel from "./LandingFiltersPanel";
import LandingFiltersPanelMobile from "./LandingFiltersPanelMobile";
import LandingHotelList from "./LandingHotelList";
import { mapLandingHotelItem } from "./mapLandingHotelItem";
import SortingPanel from "./SortingPanel";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { hotelLandingService } from "@/services/hotelServices/landing";
import { hotelBasicService } from "@/services/hotelServices/basic";
import LoadingOverlay from "@/components/ui/LoadingOverlay";
import { QueryHelper } from "@/utils/queryHelper";
import FilterLoader from "@/components/pages/hotel/search/FilterLoader";
import { Link } from "@/i18n/navigation";
import { useHotelAnalytics } from "@/components/pages/hotel/search/hooks/useHotelAnalytics";
import { useHotelSelectItemAnalytics } from "@/components/pages/hotel/search/hooks/useHotelSelectItemAnalytics";
import { useHotelLandingFilterDataLayer } from "@/components/pages/hotel/search/hooks/useHotelLandingFilterDataLayer";

/**
 * Landing page content wrapper component
 * Filtre ve hotel list state'ini yönetir
 * @param {Object} props
 * @param {string} props.landingPageName - Landing page slug (örn: "antalya")
 * @param {string} props.locale - Locale code (örn: "tr")
 */
export default function LandingPageContent({
  landingPageName,
  locale = "tr",
  initialHotels = null,
  initialMaxPageCount = 1,
  initialHotelCount = 0,
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslationDetector("hotel");

  // URL'den filtreleri ve order'ı parse et
  const parseFiltersFromURL = useCallback((paramsFromUrl) => {
    const queryHelper = new QueryHelper(paramsFromUrl);

    return {
      stars: queryHelper
        .getArray("stars", [])
        .map((s) => parseInt(s, 10))
        .filter((s) => !isNaN(s)),
      chains: queryHelper.getArray("chains", []),
      locations: queryHelper
        .getArray("locations", [])
        .map((l) => parseInt(l, 10))
        .filter((l) => !isNaN(l)),
      themes: queryHelper
        .getArray("themes", [])
        .map((themeValue) => parseInt(themeValue, 10))
        .filter((themeValue) => !isNaN(themeValue)),
      amenities: queryHelper
        .getArray("amenities", [])
        .map((a) => parseInt(a, 10))
        .filter((a) => !isNaN(a)),
      rating: (() => {
        const ratingValue = queryHelper.getString("rating", "");
        // URL'den okunan değeri decode et (örneğin: 7%2B -> 7+)
        // "0" ise "unrated" döndür, boş string ise boş string döndür
        if (ratingValue === "0") {
          return "unrated";
        }
        if (ratingValue === "") {
          return "";
        }
        return ratingValue ? decodeURIComponent(ratingValue) : "";
      })(),
    };
  }, []);

  // URL'den order değerini parse et
  const parseOrderFromURL = useCallback((paramsFromUrl) => {
    const queryHelper = new QueryHelper(paramsFromUrl);
    const orderValue = queryHelper.getString("order", "0");
    const parsed = parseInt(orderValue, 10);
    return !isNaN(parsed) ? parsed : 0;
  }, []);

  // URL'den page değerini parse et (pg parametresinden)
  const parsePageFromURL = useCallback((paramsFromUrl) => {
    const queryHelper = new QueryHelper(paramsFromUrl);
    const pageValue = queryHelper.getString("pg", "1");
    const parsed = parseInt(pageValue, 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  }, []);

  // İlk yüklemede URL'den filtreleri, order'ı ve page'i oku
  const [filters, setFilters] = useState(() =>
    parseFiltersFromURL(searchParams),
  );
  const [order, setOrder] = useState(() => parseOrderFromURL(searchParams));
  const initialPage = parsePageFromURL(searchParams);

  // initialPage değerini ref ile sakla (sadece ilk yüklemede kullanılacak, sonra değişmeyecek)
  const initialPageRef = useRef(initialPage);

  const normalizedInitialHotels = useMemo(
    () => (Array.isArray(initialHotels) ? initialHotels : []),
    [initialHotels],
  );
  const hasInitialHotels = normalizedInitialHotels.length > 0;

  const [hotels, setHotels] = useState(() => normalizedInitialHotels);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [maxPageCount, setMaxPageCount] = useState(
    () => initialMaxPageCount || 1,
  );
  const [hotelCount, setHotelCount] = useState(
    () =>
      initialHotelCount ||
      (hasInitialHotels ? normalizedInitialHotels.length : 0),
  ); // Toplam otel sayısı
  const [isLoading, setIsLoading] = useState(() => !hasInitialHotels); // İlk yükleme için true
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false); // Filtre yükleme durumu
  const isInitialMount = useRef(true);
  const [filterOptionsData, setFilterOptionsData] = useState(null);
  const [contentTitle, setContentTitle] = useState(null); // getContentBySlug (content/slug) API'den gelen Name

  const clearedFilters = useMemo(
    () => ({
      stars: [],
      chains: [],
      locations: [],
      themes: [],
      amenities: [],
      rating: "",
    }),
    [],
  );

  // API'den gelen filtre verilerini kullan (eğer varsa)
  const filterOptions = useMemo(() => {
    if (!filterOptionsData?.UserFilterOption) {
      // API verisi henüz yüklenmediyse boş obje döndür
      return {
        locations: [],
        chains: [],
        themes: [],
        amenities: [],
      };
    }

    const userFilterOption = filterOptionsData.UserFilterOption;

    // API'den gelen verileri panel'in beklediği formata çevir
    // Locations: { Id, Name, Nop, Slug } -> { Id, Name } ✓
    const locations = (userFilterOption.Locations || []).map((loc) => ({
      Id: loc.Id,
      Name: loc.Name,
    }));

    // Themes: { Id, Name, Slug } -> { Id, Name } ✓
    const themes = (userFilterOption.Themes || []).map((theme) => ({
      Id: theme.Id,
      Name: theme.Name,
    }));

    // Chains: { Name, Slug, Nop } -> { Name, Slug }
    const chains = (userFilterOption.Chains || []).map((chain) => ({
      Name: chain.Name,
      Slug: chain.Slug,
    }));

    // Amenities: { Id, Name, Slug } -> { Id, Code, Name }
    // Code olarak Id kullanılıyor (icon mapping için)
    const amenities = (userFilterOption.Amenities || []).map((amenity) => ({
      Id: amenity.Id,
      Code: amenity.Id, // Icon için Id kullanılacak
      Name: amenity.Name,
    }));

    return {
      locations,
      chains,
      themes,
      amenities,
    };
  }, [filterOptionsData]);

  // Başlık: API'den gelen Name veya slug'dan üretilen fallback (API gelene kadar)
  const slugFallbackTitle = useMemo(() => {
    return landingPageName
      .split("-")
      .map((word) => {
        if (word.length > 0) {
          return (
            word.charAt(0).toLocaleUpperCase("tr-TR") +
            word.slice(1).toLocaleLowerCase("tr-TR")
          );
        }
        return word;
      })
      .join(" ");
  }, [landingPageName]);
  const displayTitle = contentTitle ?? slugFallbackTitle;
  // Landing page content ve filtreleri getir
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const headers = {};
        const response = await hotelLandingService.getContentBySlug(
          {
            slug: landingPageName,
            languageCode: locale,
          },
          headers,
          false, // hasErrorHandler
        );

        // UserFilterOption verilerini state'e kaydet
        if (response?.UserFilterOption) {
          setFilterOptionsData(response);
        }
      } catch {
        // Hata durumunda sessizce geç
      }
    };

    fetchContent();
  }, [landingPageName, locale]);

  // Başlık için content/slug API'sinden Name al (getContentBySlug - basic)
  useEffect(() => {
    const fetchSlugContent = async () => {
      try {
        const response = await hotelBasicService.getContentBySlug(
          {
            Slug: landingPageName,
            LanguageCode: locale,
          },
          {},
          false,
        );
        if (response?.Name) {
          setContentTitle(response.Name);
        }
      } catch {
        // Hata durumunda sessizce geç, contentTitle null kalır (fallback slug title kullanılır)
      }
    };
    fetchSlugContent();
  }, [landingPageName, locale]);

  // API çağrısı yap
  const fetchHotels = useCallback(
    async (
      page,
      filterState,
      append = false,
      isFilterChange = false,
      orderValue = null,
    ) => {
      try {
        if (page === 1) {
          if (isFilterChange) {
            // Filtre değişikliğinde FilterLoader göster
            setIsFilterLoading(true);
          } else {
            // İlk yüklemede LoadingOverlay göster
            setIsLoading(true);
          }
        } else {
          setIsLoadingMore(true);
        }

        // Session bilgilerini al (client-side'da bu bilgileri cookie'den alacağız)
        // Şimdilik boş headers gönderiyoruz, gerekirse cookie'den alabiliriz
        const headers = {};

        // LanguageCode'u uppercase'e çevir (TR, EN, vb.)
        const languageCode = locale.toUpperCase();

        // UserFilterInput oluştur (camelCase olarak - servis bunu PascalCase'e çevirecek)
        // Rating değerini düzelt: "unrated" ise boş string olarak gönder
        const ratingValue =
          filterState.rating === "unrated" ? "" : filterState.rating || "";

        // Order değerini belirle: eğer orderValue verilmişse onu kullan, yoksa state'ten al
        const currentOrder = orderValue !== null ? orderValue : order || 0;

        const userFilterInput = {
          stars: filterState.stars || [],
          order: currentOrder,
          rating: ratingValue,
          chains: filterState.chains || [],
          locations: filterState.locations || [],
          themes: filterState.themes || [],
          amenities: filterState.amenities || [],
        };

        const response = await hotelLandingService.getHotelsList(
          {
            slug: landingPageName,
            page,
            userFilterInput,
            languageCode,
          },
          headers,
          false, // hasErrorHandler
        );

        // Response'u parse et
        const hotelsData = response?.Hotels || [];
        const mappedHotels = hotelsData.map(mapLandingHotelItem);
        const newMaxPageCount = response?.MaxPageCount || 1;
        const newHotelCount = response?.HotelCount || 0;

        setMaxPageCount(newMaxPageCount);
        setHotelCount(newHotelCount);

        if (append) {
          setHotels((prev) => [...prev, ...mappedHotels]);
        } else {
          setHotels(mappedHotels);
        }
      } catch (error) {
        // Not: Backend bazı senaryolarda "otel bulunamadı" için de 404 döndürüyor.
        // window.location.reload() burada sonsuz refresh döngüsü yaratır.
        // Bu yüzden 404'ü "0 sonuç" olarak ele alıyoruz.
        if (error?.response?.status === 404) {
          if (!append) {
            setHotels([]);
            setMaxPageCount(1);
            setHotelCount(0);
          }
          return;
        }

        // Diğer hata durumlarında boş liste göster
        if (!append) {
          setHotels([]);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        setIsFilterLoading(false);
      }
    },
    [landingPageName, locale, order],
  );

  // İlk yüklemede API çağrısı yap
  useEffect(() => {
    if (isInitialMount.current) {
      // İlk mount'ta fetch yap - URL'den okunan sayfa numarasını kullan
      isInitialMount.current = false;
      // Server'dan initial list geliyorsa ilk mount'ta fetch atlama
      if (hasInitialHotels) {
        setHotels(normalizedInitialHotels);
        setMaxPageCount(initialMaxPageCount || 1);
        setHotelCount(initialHotelCount || normalizedInitialHotels.length);
        setIsLoading(false);
        return;
      }

      fetchHotels(initialPage, filters, false);
    }
  }, [
    fetchHotels,
    filters,
    hasInitialHotels,
    initialHotelCount,
    initialMaxPageCount,
    initialPage,
    normalizedInitialHotels,
  ]);

  // Analytics: Landing hotel listesi için VIEW_ITEM_LIST
  const landingPassengers = useMemo(
    () => ({
      guest_total: 2,
      guest_adult_number: 2,
      guest_children_number: 0,
    }),
    [],
  );
  const { resetViewedHotels } = useHotelAnalytics({
    products: hotels,
    passengers: landingPassengers,
    currencyCode: undefined,
    itemListId: `hotel_landing_${landingPageName}`,
    itemListName: `Hotel Landing - ${landingPageName}`,
    startIndex: 1,
    crossProductPromotion: undefined,
  });

  const { trackSelectItem } = useHotelSelectItemAnalytics({
    itemListId: `hotel_landing_${landingPageName}`,
    itemListName: `Hotel Landing - ${landingPageName}`,
    passengers: landingPassengers,
    currency: "TRY",
    // basketId: undefined, // Landing'de yoksa hiç gönderilmez
    // crossProductPromotion: undefined,
  });

  const { sendSortingAppliedEvent } = useHotelLandingFilterDataLayer();

  // Not: canonical/prev/next metadata server-side `generateMetadata` ile üretiliyor.
  // Client-side document.head manipülasyonu route geçişlerinde sorun çıkardığı için kaldırıldı.

  // Filtreleri URL'e yaz
  const writeFiltersToURL = useCallback(
    (
      nextFilters,
      resetPage = false,
      orderValue = null,
      pageValue = null,
      navigationMode = "replace",
    ) => {
      const queryHelper = new QueryHelper(searchParams);

      // Array parametreleri
      queryHelper.setArray("stars", nextFilters.stars || []);
      queryHelper.setArray("chains", nextFilters.chains || []);
      queryHelper.setArray("locations", nextFilters.locations || []);
      queryHelper.setArray("themes", nextFilters.themes || []);
      queryHelper.setArray("amenities", nextFilters.amenities || []);

      // String parametreleri
      // Rating: "unrated" ise URL'e "0" olarak yaz (search sayfasındaki gibi)
      queryHelper.setString(
        "rating",
        nextFilters.rating === "unrated" ? "0" : nextFilters.rating || "",
      );

      // Order değerini yaz (sadece 0'dan farklı bir değerse)
      if (orderValue !== null && orderValue !== 0) {
        queryHelper.setString("order", orderValue.toString());
      } else if (orderValue === 0) {
        // Order 0 ise URL'den kaldır
        queryHelper.setString("order", "");
      }

      // Sayfa numarasını yaz (eğer verilmişse ve resetPage false ise) - pg parametresi olarak
      if (resetPage) {
        queryHelper.setString("pg", "");
      } else if (pageValue !== null && pageValue > 1) {
        queryHelper.setString("pg", pageValue.toString());
      }

      // URL'i güncelle
      const newQueryString = queryHelper.toString();
      const newUrl = newQueryString
        ? `${pathname}?${newQueryString}`
        : pathname;

      // load-more için route navigation tetikleme: URL'i history ile güncelle
      if (navigationMode === "history-push") {
        window.history.pushState({}, "", newUrl);
      } else if (navigationMode === "push") {
        router.push(newUrl, { scroll: false });
      } else {
        router.replace(newUrl, { scroll: false });
      }
    },
    [searchParams, pathname, router],
  );

  const handleFilterChange = (newFilters) => {
    // Analytics reset (yeni listeleme başlangıcı)
    resetViewedHotels();
    // State'i güncelle
    setFilters(newFilters);
    // Sayfa numarasını önce sıfırla (state güncellenmeden önce)
    setCurrentPage(1);
    // URL'i güncelle (page parametresini de temizle)
    // Order değeri 0 ise URL'den kaldır, değilse mevcut order'ı koru
    const currentOrder = order === 0 ? 0 : order || null;
    writeFiltersToURL(newFilters, true, currentOrder, null, "replace");
    // API çağrısı yap (filtre değişikliği olarak işaretle, her zaman 1. sayfadan başla)
    fetchHotels(1, newFilters, false, true);
  };

  // Sıralama değişikliği
  const handleSortChange = (sortId) => {
    const newOrder = parseInt(sortId, 10);
    if (isNaN(newOrder)) return;

    // Eğer aynı order değeri tekrar seçildiyse işlem yapma
    if (order === newOrder) {
      return;
    }

    sendSortingAppliedEvent(sortId);

    // Analytics reset (yeni listeleme başlangıcı)
    resetViewedHotels();
    // Order state'ini güncelle
    setOrder(newOrder);
    // Sayfa numarasını sıfırla
    setCurrentPage(1);
    // URL'i güncelle
    writeFiltersToURL(filters, true, newOrder, null, "replace");
    // API çağrısı yap (filtre değişikliği olarak işaretle, her zaman 1. sayfadan başla)
    // ÖNEMLİ: orderValue parametresi olarak newOrder'ı doğrudan gönder (state asenkron güncellendiği için)
    fetchHotels(1, filters, false, true, newOrder);
  };

  // "Daha Fazla Göster" butonuna tıklandığında
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    if (nextPage <= maxPageCount) {
      setCurrentPage(nextPage);
      // URL'e sayfa numarasını yaz
      // Pagination için sadece URL'i client-side güncelle (SSR/navigation tetikleme)
      writeFiltersToURL(filters, false, order, nextPage, "history-push");
      fetchHotels(nextPage, filters, true);
    }
  };

  // Butonun gösterilip gösterilmeyeceğini kontrol et
  const showLoadMoreButton = currentPage < maxPageCount;

  // Bir sonraki sayfayı temsil eden URL'i hesapla (sadece href için, gerçek navigation yapılmayacak)
  const nextPageHref = useMemo(() => {
    if (!showLoadMoreButton) return null;

    const nextPage = currentPage + 1;
    // Sadece pg parametresi ile yeni bir query helper oluştur (order ve diğer parametreleri dahil etme)
    const queryHelper = new QueryHelper(new URLSearchParams());

    // Sadece pg parametresini ayarla
    queryHelper.setString("pg", nextPage.toString());

    const newQueryString = queryHelper.toString();
    const currentPathname =
      typeof window !== "undefined" ? window.location.pathname : pathname;
    const siteBaseUrl = "https://www.turna.com";
    const baseUrl = `${siteBaseUrl}${currentPathname}`;

    return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
  }, [showLoadMoreButton, currentPage, pathname]);

  const handleClearFilters = useCallback(() => {
    resetViewedHotels();
    setFilters(clearedFilters);
    setOrder(0);
    setCurrentPage(1);
    writeFiltersToURL(clearedFilters, true, 0, null, "replace");
    fetchHotels(1, clearedFilters, false, true, 0);
  }, [clearedFilters, fetchHotels, resetViewedHotels, writeFiltersToURL]);

  return (
    <>
      {/* Loader - sayfa yüklenirken göster */}
      <LoadingOverlay isVisible={isLoading} />

      <div
        className={`bg-background-global relative -mx-4 block min-h-screen px-4 sm:-mx-6 sm:gap-6 sm:px-6 md:mx-0 md:flex md:px-0`}
      >
        {/* Mobile Filter Panel - block md:hidden */}
        {/* Header */}
        <div className="mb-2 md:hidden">
          <div className="leading-xl text-foreground text-xl font-semibold">
            {(() => {
              const pageSuffix =
                initialPageRef.current > 1
                  ? ` - ${t("paginationTitle", { page: initialPageRef.current })}`
                  : "";
              return `${displayTitle}${pageSuffix}`;
            })()}
          </div>
          {/* Hotel Count 
          <p className='text-foreground font-normal text-base leading-21 mt-2'>
            <span className='font-medium'>{hotelCount.toLocaleString('tr-TR')}</span> otel listelenmektedir.
          </p>
          */}
        </div>
        <div className="-mx-4 block px-4 pt-2 md:hidden">
          <LandingFiltersPanelMobile
            onSortChange={handleSortChange}
            initialSort={order.toString()}
            currentSortId={order.toString()}
            filters={filters}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions || {}}
          />
        </div>

        {/* Desktop Filter Panel - hidden md:flex (sidebar) */}
        <div
          className={`hidden w-full max-w-[294px] shrink-0 flex-col gap-2 md:flex ${isLoading && hotels.length === 0 ? "opacity-25" : ""}`}
        >
          <LandingFiltersPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
            currentSortId={order.toString()}
          />
        </div>

        <div
          className={`relative flex min-w-0 flex-1 flex-col gap-4 py-4 sm:py-4 md:py-6 lg:py-0 ${isLoading && hotels.length === 0 ? "opacity-25" : ""}`}
        >
          {/* Right Side - Hotel List Component */}
          {!isLoading && (
            <>
              {/* Header */}
              <div className="hidden md:block">
                <div className="leading-xl text-foreground text-xl font-semibold">
                  {(() => {
                    const pageSuffix =
                      initialPageRef.current > 1
                        ? ` - ${t("paginationTitle", { page: initialPageRef.current })}`
                        : "";
                    return `${displayTitle}${pageSuffix}`;
                  })()}
                </div>
                <p className="text-foreground mt-2 text-base leading-21 font-normal">
                  {t("hotelsListed", {
                    count: hotelCount.toLocaleString(locale),
                  })}
                </p>
              </div>
              {/* Sorting Panel */}
              <SortingPanel
                onSortChange={handleSortChange}
                initialSort={order.toString()}
              />
              {/* Filtre loader - filtre değiştiğinde göster */}
              <FilterLoader loading={isFilterLoading} />
              <LandingHotelList
                hotels={hotels}
                isFilterLoading={isFilterLoading}
                onSelectHotel={(hotel, index) => {
                  trackSelectItem({ hotel, index });
                }}
                itemListId={`hotel_landing_${landingPageName}`}
                itemListName={`Hotel Landing - ${landingPageName}`}
                passengers={landingPassengers}
                onClearFilters={handleClearFilters}
              />
              {showLoadMoreButton && nextPageHref && (
                <div className="">
                  <Link
                    href={nextPageHref}
                    prefetch={false}
                    onClick={(e) => {
                      // Gerçek navigation yapılmasın, sadece mevcut load more davranışı çalışsın
                      e.preventDefault();
                      if (!isLoadingMore) {
                        handleLoadMore();
                      }
                    }}
                    className="text-primary flex h-[60px] w-full cursor-pointer items-center justify-center rounded-lg bg-white text-lg leading-[24px] font-medium transition-colors hover:bg-gray-50"
                  >
                    {t("loadMore")}
                  </Link>
                </div>
              )}
              {/* Loading indicator (yeni veri yüklenirken) */}
              {isLoadingMore && hotels.length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="text-foreground-muted">{t("loading")}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
